/**
 * A slot's thumbnail that tracks its in-point: a hidden video seeks the proxy to the current start
 * (live while the trim handle scrubs) and the frame draws onto a canvas, so the card always shows the
 * clip's true first frame. The poster paints first and stays until the frame lands. Seeks queue: while
 * one is in flight the latest requested time waits, then runs, so fast scrubs settle on the last frame.
 */
import { useEffect, useRef } from 'react';
import { insideEdge } from '@/lib/playback';

export function SlotThumb({ proxyUrl, posterUrl, timeMs }: {
  proxyUrl: string | null;
  posterUrl: string | null;
  timeMs: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const posterRef = useRef<HTMLImageElement | null>(null);
  const seekingRef = useRef(false);
  const wantedRef = useRef(timeMs);

  const draw = (source: CanvasImageSource, sw: number, sh: number) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx || !sw || !sh) return;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (!w || !h) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    ctx.scale(dpr, dpr);
    const scale = Math.max(w / sw, h / sh);
    ctx.drawImage(source, (w - sw * scale) / 2, (h - sh * scale) / 2, sw * scale, sh * scale);
  };

  useEffect(() => {
    if (!posterUrl) return;
    const img = new Image();
    img.onload = () => {
      posterRef.current = img;
      if (!videoRef.current || videoRef.current.readyState < 2) draw(img, img.naturalWidth, img.naturalHeight);
    };
    img.src = posterUrl;
  }, [posterUrl]);

  useEffect(() => {
    wantedRef.current = timeMs;
    if (!proxyUrl) return;
    let v = videoRef.current;
    if (!v) {
      v = document.createElement('video');
      v.muted = true;
      v.preload = 'auto';
      videoRef.current = v;
      v.addEventListener('seeked', () => {
        seekingRef.current = false;
        const vid = videoRef.current;
        if (!vid) return;
        draw(vid, vid.videoWidth, vid.videoHeight);
        const wanted = wantedRef.current / 1000;
        if (Math.abs(vid.currentTime - wanted) > 0.04) {
          seekingRef.current = true;
          vid.currentTime = wanted;
        }
      });
    }
    const seek = () => {
      const vid = videoRef.current;
      if (!vid || seekingRef.current) return;
      seekingRef.current = true;
      // One millisecond inside the edge: a seek to where a fresh video already sits (0:00) never reports back, and nothing draws.
      vid.currentTime = insideEdge(wantedRef.current) / 1000;
    };
    if (v.src !== proxyUrl) {
      v.src = proxyUrl;
      v.addEventListener('loadedmetadata', seek, { once: true });
    } else if (v.readyState >= 1) seek();
  }, [proxyUrl, timeMs]);

  useEffect(() => () => {
    videoRef.current?.removeAttribute('src');
    videoRef.current = null;
  }, []);

  return <canvas ref={canvasRef} className="slot-thumb-canvas" role="img" aria-label="Clip first frame" />;
}
